import type {
  ActivityFeedResponse,
  ActivityQuery,
  ActivitySummaryQuery,
  ActivitySummaryResponse,
  CockpitResponse,
  ChangeSummaryResponse,
  HistoryResponse,
  RelatedResponse,
  RelatedType,
  CurateLessonRequest,
  LessonCuration,
  UnitLessonsResponse,
  GeneratePodcastRequest,
  PodcastResponse,
  AlternativeRequest,
  DelegationRequest,
  DiscussUnitRequest,
  OwnershipWriteResponse,
  SetUnitOwnerRequest,
  DiscussUnitResponse,
  UnitPage,
  UnitsListResponse,
  StoriesListResponse,
  StoryPage,
  StoryCoverageResponse,
  DiscussStoryRequest,
  DiscussStoryResponse,
  NavBadgesResponse,
  AmendmentResponse,
  ApplyAmendmentRequest,
  ApproveAmendmentRequest,
  AssistActionResponse,
  PendingAmendmentsResponse,
  AssignmentsResponse,
  AuthProviders,
  BulkReasonBatchRequest,
  RaiseCapsRequest,
  CommentRequest,
  ConsentRequest,
  CreateDecisionSessionRequest,
  DecisionSessionDetail,
  DecisionSessionsListResponse,
  DecisionDetail,
  DecisionsListResponse,
  HealthResponse,
  ImportStatusResponse,
  IntegrationsResponse,
  Locale,
  MarkNotificationsReadRequest,
  MeResponse,
  NotificationsResponse,
  PeopleResponse,
  Person,
  PulseResponse,
  PackageDriftView,
  IncomingResponse,
  DriftReposResponse,
  PackageReaction,
  CreateHoldRequest,
  DriftActionRequest,
  AcknowledgeDriftRequest,
  TrackStaffingResponse,
  SetTrackReviewerRequest,
  StaffingWriteResponse,
  CoordinatorHatRequest,
  ApproveBatchRequest,
  DriftFinding,
  ReclassificationRequest,
  RevokeOthersResponse,
  RunImportResponse,
  SessionsResponse,
  AppSettings,
  StatusReport,
  StatusReportsResponse,
  ReconfirmResponse,
  UpdateDecisionRequest,
  UpdateDecisionSessionRequest,
  UpdateAssistRequest,
  UpdateMeRequest,
  UpdatePersonRequest,
  UpdateStatusReportRequest,
  VersionResponse,
  CreateThreadRequest,
  InvestigateRequest,
  InvestigateResponse,
  Run,
  RunsListResponse,
  DecisionBriefResponse,
  ForkThreadRequest,
  Message,
  MyUsageResponse,
  PostMessageRequest,
  PromoteMessageRequest,
  PromoteResponse,
  PromotionResponse,
  ResetUsageRequest,
  ResetUsageResponse,
  SpendResponse,
  ThreadDetail,
  ThreadLink,
  ThreadSummary,
  ThreadsListResponse,
  ThreadsQuery,
  UpdateThreadRequest,
  HandoffRequest,
  HandoffResponse,
  MintTokenRequest,
  MintTokenResponse,
  TokensResponse,
  PushConfigResponse,
  PushPreferences,
  PushSubscriptionRequest,
  PushSubscriptionSummary,
  PushSubscriptionsResponse,
  AddSliceRequest,
  CloseFindingRequest,
  ConfirmFixRequest,
  SingleTapMergeRequest,
  FindingDetail,
  GateDetail,
  GatesListResponse,
  GeneratePlanRequest,
  GeneratePlanResult,
  RecordResultRequest,
  SetSignerRequest,
  SignRequest,
  TriageRequest,
  UpdateGateRequest,
  UpdateVerificationRequest,
  CreatePlanChangeRequest,
  PackagePage,
  PlanBoardResponse,
  CalendarResponse,
  PlanChangeResponse,
  PlanChangesListResponse,
  PlanConsentRequest,
  PlanEdit,
  PreviewPlanChangeResponse,
  UpdatePlanChangeRequest,
  AuthorizeReportRequest,
  AuthorizeReportResponse,
  CreateFindingRequest,
  CreateFindingResponse,
  DuplicatesResponse,
  FindingDetailWithReport,
  FindingVetState,
  KnownIssuesQuery,
  KnownIssuesResponse,
  PackageSearchResponse,
  PresignAttachmentRequest,
  PresignAttachmentResponse,
  RepointFindingRequest,
  ReportTokensResponse,
  SeenResponse,
  DecideOverrideRequest,
  DocReadResponse,
  DocRenderCanvasResponse,
  DocRenderResponse,
  DocRenderVote,
  DocsListResponse,
  GateSectionTranslation,
  ProposeOverrideRequest,
  ReapplyOverrideRequest,
  RetranslateRequest,
  TranslatedLocale,
  TranslationLookupRequest,
  TranslationLookupResponse,
  TranslationOverride,
  TranslationQueueResponse,
  TranslationStatsResponse,
  DiscussWireframeRequest,
  DiscussWireframeResponse,
  WireframeConsentRequest,
  WireframeFinalization,
  WireframeStaleBatch,
  WireframeStaleReconfirmRequest,
  WireframeStaleReconfirmResponse,
  WireframeStaleRequestResponse,
  WireframeFinalRequest,
  WireframePageResponse,
  WireframesListResponse,
  BatchResponse,
  BuildConsoleResponse,
  BuildRunResponse,
  ProposeBatchRequest,
  SizeBatchPackageRequest,
  WireframeHistoryResponse,
  StoryHistoryResponse,
  TrackHistoryResponse,
} from '@platform/shared';
import { recordFreshness } from '../offline/client';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const CSRF_COOKIE = 'brain_csrf';

export function csrfToken(): string {
  const match = document.cookie.split('; ').find((c) => c.startsWith(`${CSRF_COOKIE}=`));
  return match ? decodeURIComponent(match.slice(CSRF_COOKIE.length + 1)) : '';
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);
  headers.set('accept', 'application/json');
  if (init.body !== undefined) headers.set('content-type', 'application/json');
  if (method !== 'GET' && method !== 'HEAD') headers.set('x-csrf-token', csrfToken());
  const res = await fetch(path, { ...init, method, headers, credentials: 'same-origin' });
  // The service worker stamps answers it served from the offline cache (planning-app.md §4.4).
  if (method === 'GET') recordFreshness(path, res.headers.get('x-planning-from-cache') === 'true' ? res.headers.get('x-planning-cached-at') : null);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = text;
  }
  if (!res.ok) {
    const b = (body ?? {}) as { error?: string; message?: string };
    throw new ApiError(res.status, b.error ?? 'error', b.message ?? `Request failed (${res.status})`, body);
  }
  return body as T;
}

/** The offline outbox's key (planning-app.md §4.4), sent from the first attempt so a lost answer is replayed, not repeated. */
function idempotency(key?: string): Record<string, string> {
  return key ? { 'idempotency-key': key } : {};
}

function activityQueryString(q: ActivityQuery): string {
  const p = new URLSearchParams();
  if (q.since) p.set('since', q.since);
  if (q.until) p.set('until', q.until);
  if (q.person) p.set('person', q.person);
  if (q.kind && q.kind.length > 0) p.set('kind', q.kind.join(','));
  if (q.includeService) p.set('includeService', '1');
  if (q.limit) p.set('limit', String(q.limit));
  if (q.cursor) p.set('cursor', q.cursor);
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const api = {
  me: () => request<MeResponse>('/api/me'),
  providers: () => request<AuthProviders>('/api/auth/providers'),
  integrations: () => request<IntegrationsResponse>('/api/integrations'),
  health: () => request<HealthResponse>('/health'),
  version: () => request<VersionResponse>('/version'),
  devLogin: (email: string, name: string, locale?: string) =>
    request<{ person: Person }>('/api/auth/dev-login', { method: 'POST', body: JSON.stringify({ email, name, locale }) }),
  updateMe: (body: UpdateMeRequest) => request<{ person: Person }>('/api/me', { method: 'PATCH', body: JSON.stringify(body) }),
  updateAssist: (body: UpdateAssistRequest) => request<{ person: Person }>('/api/me/assist', { method: 'PATCH', body: JSON.stringify(body) }),
  logout: () => request<void>('/api/auth/logout', { method: 'POST' }),
  sessions: () => request<SessionsResponse>('/api/me/sessions'),
  revokeSession: (id: string) => request<void>(`/api/me/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  revokeOtherSessions: () => request<RevokeOthersResponse>('/api/me/sessions/revoke-others', { method: 'POST' }),
  /** Dev-login sessions only; Google sessions step up through GET /auth/google/step-up. */
  stepUpDev: () => request<{ stepUpFresh: boolean }>('/api/me/step-up/dev', { method: 'POST' }),
  assignments: () => request<AssignmentsResponse>('/api/me/assignments'),
  importStatus: () => request<ImportStatusResponse>('/api/import'),
  runImport: () => request<RunImportResponse>('/api/import/run', { method: 'POST', body: JSON.stringify({}) }),
  people: () => request<PeopleResponse>('/api/people'),
  updatePerson: (id: string, body: UpdatePersonRequest) => request<{ person: Person }>(`/api/people/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  decisions: () => request<DecisionsListResponse>('/api/decisions'),
  decision: (id: string) => request<DecisionDetail>(`/api/decisions/${encodeURIComponent(id)}`),
  updateDecision: (id: string, body: UpdateDecisionRequest) =>
    request<{ changes: string[]; detail: DecisionDetail }>(`/api/decisions/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  reconfirmDecision: (id: string) => request<ReconfirmResponse>(`/api/decisions/${encodeURIComponent(id)}/reconfirm`, { method: 'POST', body: JSON.stringify({}) }),
  commentDecision: (id: string, body: CommentRequest, idempotencyKey?: string) => request<{ eventId: string }>(`/api/decisions/${encodeURIComponent(id)}/comments`, { method: 'POST', body: JSON.stringify(body), headers: idempotency(idempotencyKey) }),
  addAlternative: (id: string, body: AlternativeRequest) => request<{ eventId: string }>(`/api/decisions/${encodeURIComponent(id)}/alternatives`, { method: 'POST', body: JSON.stringify(body) }),
  consent: (id: string, body: ConsentRequest, idempotencyKey?: string) => request<{ stance: string; blockedUnitOwner: boolean }>(`/api/decisions/${encodeURIComponent(id)}/consent`, { method: 'PUT', body: JSON.stringify(body), headers: idempotency(idempotencyKey) }),
  proposeReclassification: (id: string, body: ReclassificationRequest) =>
    request<{ eventId: string }>(`/api/decisions/${encodeURIComponent(id)}/reclassification`, { method: 'POST', body: JSON.stringify(body) }),
  /** Every session, or one scope's: a track's id or `programme` (v3.8b). */
  decisionSessions: (unit?: string) => request<DecisionSessionsListResponse>(`/api/sessions${unit ? `?unit=${encodeURIComponent(unit)}` : ''}`),
  decisionSession: (id: string) => request<DecisionSessionDetail>(`/api/sessions/${encodeURIComponent(id)}`),
  createDecisionSession: (body: CreateDecisionSessionRequest) => request<DecisionSessionDetail>('/api/sessions', { method: 'POST', body: JSON.stringify(body) }),
  updateDecisionSession: (id: string, body: UpdateDecisionSessionRequest) => request<DecisionSessionDetail>(`/api/sessions/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  pinToSession: (id: string, decisionId: string) => request<{ pinned: boolean }>(`/api/sessions/${encodeURIComponent(id)}/pins`, { method: 'POST', body: JSON.stringify({ decisionId }) }),
  unpinFromSession: (id: string, decisionId: string) => request<{ pinned: boolean }>(`/api/sessions/${encodeURIComponent(id)}/pins/${encodeURIComponent(decisionId)}`, { method: 'DELETE' }),
  setPackageOwner: (id: string, ownerId: string | null) => request<{ id: string; ownerId: string | null }>(`/api/packages/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ ownerId }) }),
  pulse: () => request<PulseResponse>('/api/pulse'),
  /** Activity (§8 *Activity*, v3.54): the maintainers' feed and people summary; `kind` joins as a comma list, `includeService` as `1`. */
  activity: (q: ActivityQuery) => request<ActivityFeedResponse>(`/api/activity${activityQueryString(q)}`),
  activitySummary: (q: ActivitySummaryQuery) => request<ActivitySummaryResponse>(`/api/activity/people${activityQueryString(q)}`),
  pulseCockpit: (since?: string | null) => request<CockpitResponse>(`/api/pulse/cockpit${since ? `?since=${encodeURIComponent(since)}` : ''}`),
  statusReports: () => request<StatusReportsResponse>('/api/status-reports'),
  statusReport: (id: string) => request<{ report: StatusReport }>(`/api/status-reports/${encodeURIComponent(id)}`),
  draftStatusReport: () => request<{ report: StatusReport }>('/api/status-reports/draft', { method: 'POST', body: JSON.stringify({}) }),
  updateStatusReport: (id: string, body: UpdateStatusReportRequest) => request<{ report: StatusReport }>(`/api/status-reports/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  sendStatusReport: (id: string) => request<{ report: StatusReport }>(`/api/status-reports/${encodeURIComponent(id)}/send`, { method: 'POST', body: JSON.stringify({}) }),
  notifications: () => request<NotificationsResponse>('/api/notifications'),
  markNotificationsRead: (body: MarkNotificationsReadRequest) => request<{ marked: number }>('/api/notifications/read', { method: 'POST', body: JSON.stringify(body) }),
  settings: () => request<{ settings: AppSettings }>('/api/settings'),
  updateSettings: (body: Partial<AppSettings>) => request<{ settings: AppSettings }>('/api/settings', { method: 'PATCH', body: JSON.stringify(body) }),

  threads: (q: ThreadsQuery = {}) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== '') params.set(k, String(v));
    const qs = params.toString();
    return request<ThreadsListResponse>(`/api/threads${qs ? `?${qs}` : ''}`);
  },
  createThread: (body: CreateThreadRequest) => request<ThreadDetail>('/api/threads', { method: 'POST', body: JSON.stringify(body) }),
  thread: (id: string) => request<ThreadDetail>(`/api/threads/${encodeURIComponent(id)}`),
  markThreadRead: (id: string) => request<void>(`/api/threads/${encodeURIComponent(id)}/read`, { method: 'POST', body: JSON.stringify({}) }),
  updateThread: (id: string, body: UpdateThreadRequest) => request<ThreadDetail>(`/api/threads/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteThread: (id: string) => request<void>(`/api/threads/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  postMessage: (id: string, body: PostMessageRequest) =>
    request<{ messageId: string; assistantMessageId: string | null; language?: string | null }>(`/api/threads/${encodeURIComponent(id)}/messages`, { method: 'POST', body: JSON.stringify(body) }),
  forkThread: (id: string, body: ForkThreadRequest) => request<{ threadId: string }>(`/api/threads/${encodeURIComponent(id)}/fork`, { method: 'POST', body: JSON.stringify(body) }),
  pinMessage: (id: string, messageId: string, pinned: boolean) =>
    request<{ message: Message | null }>(`/api/threads/${encodeURIComponent(id)}/messages/${encodeURIComponent(messageId)}/pin`, { method: 'POST', body: JSON.stringify({ pinned }) }),
  promoteMessage: (id: string, messageId: string, body: PromoteMessageRequest) =>
    request<PromoteResponse>(`/api/threads/${encodeURIComponent(id)}/messages/${encodeURIComponent(messageId)}/promote`, { method: 'POST', body: JSON.stringify(body) }),
  setThreadLink: (id: string, link: ThreadLink, on: boolean) => request<ThreadSummary>(`/api/threads/${encodeURIComponent(id)}/links`, { method: on ? 'PUT' : 'DELETE', body: JSON.stringify(link) }),
  addParticipant: (id: string, personId: string) => request<ThreadSummary>(`/api/threads/${encodeURIComponent(id)}/participants`, { method: 'POST', body: JSON.stringify({ personId }) }),
  stopMessage: (id: string, messageId: string) =>
    request<{ stopped: boolean; message: Message | null }>(`/api/threads/${encodeURIComponent(id)}/messages/${encodeURIComponent(messageId)}/stop`, { method: 'POST', body: JSON.stringify({}) }),
  /** You → Tokens (v1.9): the MCP bearer tokens; the mint needs a fresh step-up and returns the clear token once. */
  tokens: () => request<TokensResponse>('/api/tokens'),
  mintToken: (body: MintTokenRequest) => request<MintTokenResponse>('/api/tokens', { method: 'POST', body: JSON.stringify(body) }),
  revokeToken: (id: string) => request<void>(`/api/tokens/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  pushConfig: () => request<PushConfigResponse>('/api/push/config'),
  pushSubscriptions: () => request<PushSubscriptionsResponse>('/api/push/subscriptions'),
  subscribePush: (body: PushSubscriptionRequest) => request<{ subscription: PushSubscriptionSummary }>('/api/push/subscriptions', { method: 'POST', body: JSON.stringify(body) }),
  unsubscribePush: (endpoint: string) => request<void>('/api/push/subscriptions', { method: 'DELETE', body: JSON.stringify({ endpoint }) }),
  updatePushPreferences: (preferences: PushPreferences) => request<{ preferences: PushPreferences }>('/api/push/preferences', { method: 'PATCH', body: JSON.stringify({ preferences }) }),
  handoff: (body: HandoffRequest) => request<HandoffResponse>('/api/me/handoff', { method: 'POST', body: JSON.stringify(body) }),
  usageMe: () => request<MyUsageResponse>('/api/usage/me'),
  usageSpend: () => request<SpendResponse>('/api/usage/spend'),
  resetUsage: (body: ResetUsageRequest) => request<ResetUsageResponse>('/api/usage/reset', { method: 'POST', body: JSON.stringify(body) }),
  wireframes: () => request<WireframesListResponse>('/api/wireframes'),
  wireframe: (file: string) => request<WireframePageResponse>(`/api/wireframes/${encodeURIComponent(file)}`),
  discussWireframe: (file: string, body: DiscussWireframeRequest) => request<DiscussWireframeResponse>(`/api/wireframes/${encodeURIComponent(file)}/threads`, { method: 'POST', body: JSON.stringify(body) }),
  wireframeFinalization: (file: string) => request<WireframeFinalization>(`/api/wireframes/${encodeURIComponent(file)}/finalization`),
  setWireframeInReview: (file: string) => request<WireframeFinalization>(`/api/wireframes/${encodeURIComponent(file)}/review`, { method: 'POST', body: '{}' }),
  recordWireframeConsent: (file: string, body: WireframeConsentRequest) => request<WireframeFinalization>(`/api/wireframes/${encodeURIComponent(file)}/consents`, { method: 'POST', body: JSON.stringify(body) }),
  markWireframeFinal: (file: string, body: WireframeFinalRequest) => request<WireframeFinalization>(`/api/wireframes/${encodeURIComponent(file)}/final`, { method: 'POST', body: JSON.stringify(body) }),
  reopenWireframe: (file: string) => request<WireframeFinalization>(`/api/wireframes/${encodeURIComponent(file)}/reopen`, { method: 'POST', body: '{}' }),
  reconfirmWireframe: (file: string, renderHash: string) => request<WireframeFinalization>(`/api/wireframes/${encodeURIComponent(file)}/reconfirm`, { method: 'POST', body: JSON.stringify({ renderHash }) }),
  wireframeStale: () => request<WireframeStaleBatch>('/api/wireframes/stale'),
  requestStaleWireframeConsents: () => request<WireframeStaleRequestResponse>('/api/wireframes/stale/request-consents', { method: 'POST', body: '{}' }),
  reconfirmStaleWireframes: (pages: WireframeStaleReconfirmRequest['pages']) => request<WireframeStaleReconfirmResponse>('/api/wireframes/stale/reconfirm', { method: 'POST', body: JSON.stringify({ pages }) }),
  investigate: (threadId: string, body: InvestigateRequest) => request<InvestigateResponse>(`/api/threads/${encodeURIComponent(threadId)}/investigate`, { method: 'POST', body: JSON.stringify(body) }),
  threadRuns: (threadId: string) => request<RunsListResponse>(`/api/threads/${encodeURIComponent(threadId)}/runs`),
  runs: (status?: string, limit?: number) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (limit) params.set('limit', String(limit));
    const qs = params.toString();
    return request<RunsListResponse>(`/api/runs${qs ? `?${qs}` : ''}`);
  },
  cancelRun: (id: string) => request<{ run: Run }>(`/api/runs/${encodeURIComponent(id)}/cancel`, { method: 'POST', body: JSON.stringify({}) }),
  retryRun: (id: string) => request<{ run: Run }>(`/api/runs/${encodeURIComponent(id)}/retry`, { method: 'POST', body: JSON.stringify({}) }),
  promotion: (id: string) => request<PromotionResponse>(`/api/promotions/${encodeURIComponent(id)}`),
  amendment: (id: string) => request<AmendmentResponse>(`/api/amendments/${encodeURIComponent(id)}`),
  confirmAmendment: (id: string) => request<AmendmentResponse>(`/api/amendments/${encodeURIComponent(id)}/confirm`, { method: 'POST', body: JSON.stringify({}) }),
  withdrawAmendment: (id: string) => request<AmendmentResponse>(`/api/amendments/${encodeURIComponent(id)}/withdraw`, { method: 'POST', body: JSON.stringify({}) }),
  // PA64 (v3.15d): Apply, a maintainer's Looks good, Undo.
  applyAmendment: (id: string, seen: ApplyAmendmentRequest = {}) => request<AmendmentResponse>(`/api/amendments/${encodeURIComponent(id)}/apply`, { method: 'POST', body: JSON.stringify(seen) }),
  approveAmendment: (id: string, seen: ApproveAmendmentRequest) => request<AmendmentResponse>(`/api/amendments/${encodeURIComponent(id)}/approve`, { method: 'POST', body: JSON.stringify(seen) }),
  undoAmendment: (id: string) => request<AmendmentResponse>(`/api/amendments/${encodeURIComponent(id)}/undo`, { method: 'POST', body: JSON.stringify({}) }),
  discardAmendment: (id: string) => request<AmendmentResponse>(`/api/amendments/${encodeURIComponent(id)}/discard`, { method: 'POST', body: JSON.stringify({}) }),
  recheckAmendment: (id: string) => request<AmendmentResponse>(`/api/amendments/${encodeURIComponent(id)}/recheck`, { method: 'POST', body: JSON.stringify({}) }),
  runAssistAction: (id: string) => request<AssistActionResponse>(`/api/assist/actions/${encodeURIComponent(id)}/run`, { method: 'POST', body: JSON.stringify({}) }),
  declineAssistAction: (id: string) => request<AssistActionResponse>(`/api/assist/actions/${encodeURIComponent(id)}/decline`, { method: 'POST', body: JSON.stringify({}) }),
  undoAssistAction: (id: string) => request<AssistActionResponse>(`/api/assist/actions/${encodeURIComponent(id)}/undo`, { method: 'POST', body: JSON.stringify({}) }),
  pendingAmendments: (q: { decisions?: string[]; packages?: string[] }) => {
    const params = new URLSearchParams();
    if (q.decisions?.length) params.set('decisions', q.decisions.join(','));
    if (q.packages?.length) params.set('packages', q.packages.join(','));
    return request<PendingAmendmentsResponse>(`/api/amendments/pending?${params.toString()}`);
  },
  decisionBrief: (id: string, locale?: Locale) => request<DecisionBriefResponse>(`/api/decisions/${encodeURIComponent(id)}/brief${locale ? `?locale=${locale}` : ''}`),
  requestDecisionBrief: (id: string, body: { locale?: Locale; regenerate?: boolean }) =>
    request<DecisionBriefResponse & { queued: boolean }>(`/api/decisions/${encodeURIComponent(id)}/brief`, { method: 'POST', body: JSON.stringify(body) }),

  // The verification desk (planning-app.md §3.5).
  gates: () => request<GatesListResponse>('/api/verify'),
  gate: (phase: string) => request<GateDetail>(`/api/verify/gates/${encodeURIComponent(phase)}`),
  generatePlan: (phase: string, body: GeneratePlanRequest = {}) => request<GeneratePlanResult>(`/api/verify/gates/${encodeURIComponent(phase)}/plan`, { method: 'POST', body: JSON.stringify(body) }),
  updateGate: (phase: string, body: UpdateGateRequest) => request<GateDetail>(`/api/verify/gates/${encodeURIComponent(phase)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  setSigner: (phase: string, body: SetSignerRequest) => request<GateDetail>(`/api/verify/gates/${encodeURIComponent(phase)}/signers`, { method: 'POST', body: JSON.stringify(body) }),
  addSlice: (phase: string, body: AddSliceRequest) => request<{ id: string }>(`/api/verify/gates/${encodeURIComponent(phase)}/slices`, { method: 'POST', body: JSON.stringify(body) }),
  signGate: (phase: string, body: SignRequest) => request<GateDetail>(`/api/verify/gates/${encodeURIComponent(phase)}/sign`, { method: 'POST', body: JSON.stringify(body) }),
  updateVerification: (id: string, body: UpdateVerificationRequest) => request<{ id: string }>(`/api/verify/items/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  recordResult: (id: string, body: RecordResultRequest, idempotencyKey?: string) =>
    request<{ id: string; findingId: string | null }>(`/api/verify/items/${encodeURIComponent(id)}/result`, { method: 'POST', body: JSON.stringify(body), headers: idempotency(idempotencyKey) }),
  confirmResult: (id: string, idempotencyKey?: string) => request<{ id: string }>(`/api/verify/items/${encodeURIComponent(id)}/confirm`, { method: 'POST', body: JSON.stringify({}), headers: idempotency(idempotencyKey) }),
  finding: (id: string) => request<FindingDetailWithReport>(`/api/verify/findings/${encodeURIComponent(id)}`),
  takeFinding: (id: string, personId?: string) => request<FindingDetail>(`/api/verify/findings/${encodeURIComponent(id)}/take`, { method: 'POST', body: JSON.stringify(personId ? { personId } : {}) }),
  triageFinding: (id: string, body: TriageRequest) => request<FindingDetail & { reviews: string[] }>(`/api/verify/findings/${encodeURIComponent(id)}/triage`, { method: 'POST', body: JSON.stringify(body) }),
  createRework: (id: string) => request<{ reworkPackageId: string; detail: FindingDetail }>(`/api/verify/findings/${encodeURIComponent(id)}/rework`, { method: 'POST', body: JSON.stringify({}) }),
  closeFinding: (id: string, body: CloseFindingRequest) => request<FindingDetail>(`/api/verify/findings/${encodeURIComponent(id)}/close`, { method: 'POST', body: JSON.stringify(body) }),
  // v3.53 the vet: a maintainer's *Dispatch fix anyway*, and *Vet again*.
  autofixFinding: (id: string) => request<{ reworkPackageId: string; detail: FindingDetail; vet: FindingVetState | null }>(`/api/verify/findings/${encodeURIComponent(id)}/autofix`, { method: 'POST', body: JSON.stringify({}) }),
  unlinkFixMr: (id: string) => request<{ mr: number; detail: FindingDetail }>(`/api/verify/findings/${encodeURIComponent(id)}/unlink-fix`, { method: 'POST', body: JSON.stringify({}) }),
  revetFinding: (id: string) => request<{ vet: FindingVetState | null }>(`/api/verify/findings/${encodeURIComponent(id)}/vet`, { method: 'POST', body: JSON.stringify({}) }),
  // The self-fix loop (v3.3, PA24): the reporter's answer on the review app, and the maintainer's single-tap merge (step-up).
  confirmFix: (id: string, body: ConfirmFixRequest) => request<FindingDetailWithReport>(`/api/verify/findings/${encodeURIComponent(id)}/confirm-fix`, { method: 'POST', body: JSON.stringify(body) }),
  mergeFix: (id: string, body: SingleTapMergeRequest) => request<{ mergeCommitSha: string | null; detail: FindingDetail; report: FindingDetailWithReport['report'] }>(`/api/verify/findings/${encodeURIComponent(id)}/merge`, { method: 'POST', body: JSON.stringify(body) }),

  // Ad-hoc findings and the report widget (planning-app.md §3.5): the *You* form, Known issues, report tokens.
  knownIssues: (q: KnownIssuesQuery = {}) => {
    const params = new URLSearchParams();
    if (q.target) params.set('target', q.target);
    if (q.unit) params.set('unit', q.unit);
    const qs = params.toString();
    return request<KnownIssuesResponse>(`/api/findings${qs ? `?${qs}` : ''}`);
  },
  reportFinding: (body: CreateFindingRequest) => request<CreateFindingResponse>('/api/findings', { method: 'POST', body: JSON.stringify(body) }),
  findingDuplicates: (q: { route?: string; target?: 'brain' | 'planning-app' }) => {
    const params = new URLSearchParams();
    if (q.route) params.set('route', q.route);
    if (q.target) params.set('target', q.target);
    const qs = params.toString();
    return request<DuplicatesResponse>(`/api/findings/duplicates${qs ? `?${qs}` : ''}`);
  },
  seenFinding: (id: string) => request<SeenResponse>(`/api/findings/${encodeURIComponent(id)}/seen`, { method: 'POST', body: JSON.stringify({}) }),
  repointFinding: (id: string, body: RepointFindingRequest) => request<{ id: string; packageId: string }>(`/api/findings/${encodeURIComponent(id)}/package`, { method: 'PATCH', body: JSON.stringify(body) }),
  searchPackages: (q: string) => request<PackageSearchResponse>(`/api/findings/packages?q=${encodeURIComponent(q)}`),
  presignAttachment: (body: PresignAttachmentRequest) => request<PresignAttachmentResponse>('/api/findings/attachments', { method: 'POST', body: JSON.stringify(body) }),
  reportTokens: () => request<ReportTokensResponse>('/api/report-tokens'),
  authorizeReport: (body: AuthorizeReportRequest) => request<AuthorizeReportResponse>('/api/report-tokens', { method: 'POST', body: JSON.stringify(body) }),
  revokeReportToken: (id: string) => request<void>(`/api/report-tokens/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // The Plan board (planning-app.md §3.3).
  planBoard: () => request<PlanBoardResponse>('/api/plan'),
  planCalendar: () => request<CalendarResponse>('/api/plan/calendar'),
  planPackage: (id: string) => request<PackagePage>(`/api/plan/packages/${encodeURIComponent(id)}`),
  // The nav badges (planning-app.md §3 *Navigation from v3.8*, PA62a): counts next to the items behind More.
  nav: () => request<NavBadgesResponse>('/api/nav'),
  // Track pages (planning-app.md §3.3 *Track pages*, PA58, v2.13): the list by lane, one unit's page, Discuss this track.
  planUnits: () => request<UnitsListResponse>('/api/plan/units'),
  planUnit: (id: string) => request<UnitPage>(`/api/plan/units/${encodeURIComponent(id)}`),
  discussUnit: (id: string, body: DiscussUnitRequest) => request<DiscussUnitResponse>(`/api/plan/units/${encodeURIComponent(id)}/threads`, { method: 'POST', body: JSON.stringify(body) }),
  // Track ownership (planning-app.md §3.3 *Track ownership*, PA61, v3.7): the owner, the delegations, Take it. Behind the step-up.
  setUnitOwner: (id: string, body: SetUnitOwnerRequest) => request<OwnershipWriteResponse>(`/api/plan/units/${encodeURIComponent(id)}/owner`, { method: 'PUT', body: JSON.stringify(body) }),
  addDelegation: (id: string, body: DelegationRequest) => request<OwnershipWriteResponse & { id: string }>(`/api/plan/units/${encodeURIComponent(id)}/delegations`, { method: 'POST', body: JSON.stringify(body) }),
  revokeDelegation: (id: string, delegationId: string) => request<OwnershipWriteResponse>(`/api/plan/units/${encodeURIComponent(id)}/delegations/${encodeURIComponent(delegationId)}`, { method: 'DELETE' }),
  takeDecision: (id: string) => request<OwnershipWriteResponse>(`/api/decisions/${encodeURIComponent(id)}/take`, { method: 'POST' }),
  // User Stories (planning-app-information-architecture.md §3, v3.9a): the list (filtered client side), one story's page, the per-track coverage, Discuss / Propose a change.
  stories: (includeRetired = false) => request<StoriesListResponse>(`/api/stories${includeRetired ? '?retired=1' : ''}`),
  story: (id: string) => request<StoryPage>(`/api/stories/${encodeURIComponent(id)}`),
  storyCoverage: () => request<StoryCoverageResponse>('/api/stories/coverage'),
  /** The entity rail's projection (IA §4.1, v3.9b): what a track, story, wireframe page or document links to, in six sections. */
  related: (type: RelatedType, id: string) => request<RelatedResponse>(`/api/related/${type}/${encodeURIComponent(id)}`),
  discussStory: (id: string, body: DiscussStoryRequest) => request<DiscussStoryResponse>(`/api/stories/${encodeURIComponent(id)}/threads`, { method: 'POST', body: JSON.stringify(body) }),
  // Upstream drift (planning-app.md §3.4, v3.4): the package's drift view, the Incoming panel, the listener's state per repository.
  planPackageDrift: (id: string) => request<PackageDriftView>(`/api/plan/packages/${encodeURIComponent(id)}/drift`),
  planIncoming: (filter: { unit?: string; repo?: string } = {}) => {
    const qs = new URLSearchParams(Object.entries(filter).filter((e): e is [string, string] => typeof e[1] === 'string' && e[1] !== '')).toString();
    return request<IncomingResponse>(`/api/plan/incoming${qs ? `?${qs}` : ''}`);
  },
  planDriftRepos: () => request<DriftReposResponse>('/api/plan/drift/repos'),
  // v3.5 (PA54–PA56): the human side of the base moving — holds, the building · drift decision, the acknowledgement on the batch row, the plan change from a drift finding. All behind the step-up.
  createHold: (id: string, body: CreateHoldRequest) => request<{ id: string; reaction: PackageReaction }>(`/api/plan/packages/${encodeURIComponent(id)}/holds`, { method: 'POST', body: JSON.stringify(body) }),
  releaseHold: (id: string, holdId: string) => request<{ id: string; reaction: PackageReaction }>(`/api/plan/packages/${encodeURIComponent(id)}/holds/${encodeURIComponent(holdId)}`, { method: 'DELETE' }),
  driftAction: (id: string, body: DriftActionRequest) => request<{ action: DriftActionRequest['action']; runId: string | null; holdId: string | null; reaction: PackageReaction }>(`/api/plan/packages/${encodeURIComponent(id)}/drift/actions`, { method: 'POST', body: JSON.stringify(body) }),
  // v3.37 (PA61 amended): track staffing — the ten tracks with the most ready weight, their owner and reviewer hats, the coordinator identity; writes behind the step-up.
  buildStaffing: () => request<TrackStaffingResponse>('/api/build/staffing'),
  setTrackReviewer: (unit: string, body: SetTrackReviewerRequest) => request<StaffingWriteResponse>(`/api/build/staffing/tracks/${encodeURIComponent(unit)}/reviewers`, { method: 'PUT', body: JSON.stringify(body) }),
  removeTrackReviewer: (unit: string, personId: string) => request<StaffingWriteResponse>(`/api/build/staffing/tracks/${encodeURIComponent(unit)}/reviewers/${encodeURIComponent(personId)}`, { method: 'DELETE' }),
  coordinatorHat: (body: CoordinatorHatRequest) => request<StaffingWriteResponse>('/api/build/staffing/coordinator', { method: 'POST', body: JSON.stringify(body) }),
  acknowledgeDrift: (batchId: string, pkg: string, body: AcknowledgeDriftRequest) => request<BatchResponse>(`/api/build/batches/${encodeURIComponent(batchId)}/packages/${encodeURIComponent(pkg)}/acknowledge`, { method: 'POST', body: JSON.stringify(body) }),
  proposePlanChangeFromFinding: (id: string) => request<{ changeId: string; drift: DriftFinding | null }>(`/api/verify/findings/${encodeURIComponent(id)}/propose-plan-change`, { method: 'POST', body: JSON.stringify({}) }),
  previewPlanChange: (edits: PlanEdit[]) => request<PreviewPlanChangeResponse>('/api/plan/preview', { method: 'POST', body: JSON.stringify({ edits }) }),
  planChanges: (all = false) => request<PlanChangesListResponse>(`/api/plan/changes${all ? '?all=1' : ''}`),
  planChange: (id: string) => request<PlanChangeResponse>(`/api/plan/changes/${encodeURIComponent(id)}`),
  createPlanChange: (body: CreatePlanChangeRequest) => request<PlanChangeResponse>('/api/plan/changes', { method: 'POST', body: JSON.stringify(body) }),
  updatePlanChange: (id: string, body: UpdatePlanChangeRequest) => request<PlanChangeResponse>(`/api/plan/changes/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  consentPlanChange: (id: string, body: PlanConsentRequest) => request<PlanChangeResponse & { affected: boolean }>(`/api/plan/changes/${encodeURIComponent(id)}/consent`, { method: 'PUT', body: JSON.stringify(body) }),
  proposePlanChange: (id: string) => request<PlanChangeResponse>(`/api/plan/changes/${encodeURIComponent(id)}/propose`, { method: 'POST', body: JSON.stringify({}) }),
  withdrawPlanChange: (id: string) => request<PlanChangeResponse>(`/api/plan/changes/${encodeURIComponent(id)}/withdraw`, { method: 'POST', body: JSON.stringify({}) }),

  // Document translation (planning-app.md §3.7 Layer 3): the cache read, the docs reader, corrections and the reviewer's queue.
  translationsLookup: (body: TranslationLookupRequest) => request<TranslationLookupResponse>('/api/translations/lookup', { method: 'POST', body: JSON.stringify(body) }),
  translationStats: () => request<TranslationStatsResponse>('/api/translations/stats'),
  translationQueue: (locale: TranslatedLocale) => request<TranslationQueueResponse>(`/api/translations/queue/${locale}`),
  gateTranslationSections: (phase: string) => request<{ sections: GateSectionTranslation[] }>(`/api/translations/gates/${encodeURIComponent(phase)}/sections`),
  retranslate: (body: RetranslateRequest) => request<{ queued: number }>('/api/translations/retranslate', { method: 'POST', body: JSON.stringify(body) }),
  verifyTranslation: (body: { unitKey: string; sourceHash: string; locale: TranslatedLocale }) => request<{ ok: true }>('/api/translations/verify', { method: 'POST', body: JSON.stringify(body) }),
  proposeTranslationOverride: (body: ProposeOverrideRequest) => request<{ override: TranslationOverride }>('/api/translations/overrides', { method: 'POST', body: JSON.stringify(body) }),
  decideTranslationOverride: (id: string, body: DecideOverrideRequest) => request<{ override: TranslationOverride }>(`/api/translations/overrides/${encodeURIComponent(id)}/decide`, { method: 'POST', body: JSON.stringify(body) }),
  reapplyTranslationOverride: (id: string, body: ReapplyOverrideRequest) => request<{ override: TranslationOverride }>(`/api/translations/overrides/${encodeURIComponent(id)}/reapply`, { method: 'POST', body: JSON.stringify(body) }),
  docs: () => request<DocsListResponse>('/api/docs'),
  docRead: (path: string, locale?: string) => request<DocReadResponse>(`/api/docs/read?path=${encodeURIComponent(path)}${locale ? `&locale=${locale}` : ''}`),
  /** How a document changed through time (v3.29): the versions and the selected one's diff and summary; the poll while the summary is written. */
  docHistory: (path: string, sha?: string) => request<HistoryResponse>(`/api/docs/history?path=${encodeURIComponent(path)}${sha ? `&sha=${encodeURIComponent(sha)}` : ''}`),
  docHistorySummary: (path: string, sha: string) => request<ChangeSummaryResponse>(`/api/docs/history/summary?path=${encodeURIComponent(path)}&sha=${encodeURIComponent(sha)}`),
  wireframeHistory: (file: string, pick: { sha?: string; event?: string } = {}) => request<WireframeHistoryResponse>(`/api/wireframes/${encodeURIComponent(file)}/history${pick.event ? `?event=${encodeURIComponent(pick.event)}` : pick.sha ? `?sha=${encodeURIComponent(pick.sha)}` : ''}`),
  wireframeHistorySummary: (file: string, sha: string) => request<ChangeSummaryResponse>(`/api/wireframes/${encodeURIComponent(file)}/history/summary?sha=${encodeURIComponent(sha)}`),
  /** How a story changed through time (v3.31): the versions of user-stories.md that changed its block, the selected one's word diff. */
  storyHistory: (id: string, sha?: string) => request<StoryHistoryResponse>(`/api/stories/${encodeURIComponent(id)}/history${sha ? `?sha=${encodeURIComponent(sha)}` : ''}`),
  trackHistory: (id: string, pick: { sha?: string; change?: string; import?: string } = {}) => {
    const q = new URLSearchParams();
    if (pick.sha) q.set('sha', pick.sha);
    if (pick.change) q.set('change', pick.change);
    if (pick.import) q.set('import', pick.import);
    const qs = q.toString();
    return request<TrackHistoryResponse>(`/api/plan/units/${encodeURIComponent(id)}/history${qs ? `?${qs}` : ''}`);
  },
  docRender: (path: string, locale?: string) => request<DocRenderResponse>(`/api/docs/render?path=${encodeURIComponent(path)}${locale ? `&locale=${locale}` : ''}`),
  /** A ready canvas's compiled module (v2.14b), handed to the SDK frame as `canvas:init`. */
  docRenderCanvas: (canvasUrl: string) => request<DocRenderCanvasResponse>(canvasUrl),
  rateRender: (id: string, vote: DocRenderVote | null) => request<{ vote: DocRenderVote | null }>(`/api/docs/render/${id}/rating`, { method: 'POST', body: JSON.stringify({ vote }) }),
  retryRender: (id: string) => request<{ queued: boolean }>(`/api/docs/render/${id}/retry`, { method: 'POST', body: '{}' }),
  // Podcast mode (v2.15): the episode's state and a maintainer's Generate / Regenerate.
  docPodcast: (path: string, locale?: string) => request<PodcastResponse>(`/api/docs/podcast?path=${encodeURIComponent(path)}${locale ? `&locale=${locale}` : ''}`),
  generatePodcast: (body: GeneratePodcastRequest) => request<PodcastResponse>('/api/docs/podcast', { method: 'POST', body: JSON.stringify(body) }),

  // The Build console (planning-app.md §3.4, §13 v3.0): the week's batch, its sizing and approval, the runs.
  build: () => request<BuildConsoleResponse>('/api/build'),
  buildBatch: (id: string) => request<BatchResponse>(`/api/build/batches/${encodeURIComponent(id)}`),
  proposeBatch: (body: ProposeBatchRequest = {}) => request<BatchResponse>('/api/build/batches', { method: 'POST', body: JSON.stringify(body) }),
  sizeBatchPackage: (id: string, pkg: string, body: SizeBatchPackageRequest) => request<BatchResponse>(`/api/build/batches/${encodeURIComponent(id)}/packages/${encodeURIComponent(pkg)}/sizing`, { method: 'POST', body: JSON.stringify(body) }),
  /** v3.34 (PA48 (d)): the lead's exception on every L row still waiting for a sizing, one shared text. */
  bulkReasonBatch: (id: string, body: BulkReasonBatchRequest) => request<BatchResponse>(`/api/build/batches/${encodeURIComponent(id)}/sizing`, { method: 'POST', body: JSON.stringify(body) }),
  approveBatch: (id: string, body: ApproveBatchRequest = {}) => request<BatchResponse>(`/api/build/batches/${encodeURIComponent(id)}/approve`, { method: 'POST', body: JSON.stringify(body) }),
  /** v3.19 (PA66): the off switch — the loop's merges on this batch stop at the next poll. */
  stopLoopMerge: (id: string) => request<BatchResponse>(`/api/build/batches/${encodeURIComponent(id)}/loop-merge`, { method: 'POST', body: JSON.stringify({ enabled: false }) }),
  /** v3.36 (PA42 note 2026-11-01): *raise for this batch* — a project's running cap and / or open-MR ceiling, for the approved batch alone. */
  raiseBatchCaps: (id: string, body: RaiseCapsRequest) => request<BatchResponse>(`/api/build/batches/${encodeURIComponent(id)}/caps`, { method: 'POST', body: JSON.stringify(body) }),
  cancelBatch: (id: string, reason?: string) => request<BatchResponse>(`/api/build/batches/${encodeURIComponent(id)}/cancel`, { method: 'POST', body: JSON.stringify(reason ? { reason } : {}) }),
  buildRun: (id: string) => request<BuildRunResponse>(`/api/build/runs/${encodeURIComponent(id)}`),
  resumeBuildRun: (id: string, note?: string) => request<BuildRunResponse>(`/api/build/runs/${encodeURIComponent(id)}/resume`, { method: 'POST', body: JSON.stringify(note ? { note } : {}) }),
  // v3.13b: the learnings channel — a unit's lessons as the next run would get them, and a maintainer's pin / retire.
  unitLessons: (unit: string) => request<UnitLessonsResponse>(`/api/build/lessons?unit=${encodeURIComponent(unit)}`),
  curateLesson: (body: CurateLessonRequest) => request<LessonCuration>('/api/build/lessons', { method: 'POST', body: JSON.stringify(body) }),
};

/** The docs reader's exports (planning-app.md §3.7): Markdown as a download, the print page in a tab. */
export const docExportUrl = (path: string, locale: string, format: 'md' | 'html') => `/api/docs/export?path=${encodeURIComponent(path)}&locale=${locale}&format=${format}`;
/** The frame's URL with the theme (and `print=1` for the print tab) — `frameUrl` as the API returned it. */

/** Downloads served by the API (the browser follows them with the session cookie). */
export const threadExportUrl = (id: string, format: 'md' | 'json') => `/api/threads/${encodeURIComponent(id)}/export?format=${format}`;
export const threadsExportUrl = (format: 'md' | 'json') => `/api/threads/export?format=${format}`;

/** The tracker's export, served as a download by the API. */
export const DECISIONS_STATE_EXPORT_URL = '/api/decisions/export/decisions-state.json';
